import { type ColumnFiltersState } from '@apps/web/lib/table';
import { type InhouseRecommendationRequestParams } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type RecommendationFilterNode } from '@libraries/typescript/api_client/schema_types';

type SetOrStatusDimension =
    | 'tags'
    | 'roles'
    | 'office'
    | 'gender'
    | 'language'
    | 'zip'
    | 'preferred_availability'
    | 'scheduled_availability'
    | 'overtime'
    | 'preferred';

type NumberDimension = 'distance' | 'contact_attempts' | 'star_rating';

type SetFilterOperator =
    | 'includes_any'
    | 'includes_all'
    | 'excludes_any'
    | 'excludes_all'
    | 'is'
    | 'is_not';

type NumberFilterOperator = 'lte' | 'gte' | 'range';

type FacetFilterMode = 'any' | 'all';

export type SetFilterRow = {
    readonly id: string;
    readonly dimension: SetOrStatusDimension;
    readonly operator: SetFilterOperator;
    readonly values: Array<string>;
};

export type NumberFilterRow = {
    readonly id: string;
    readonly dimension: NumberDimension;
    readonly operator: NumberFilterOperator;
    readonly value: number | null;
    readonly maxValue?: number | null;
};

export type RecommendationFilterRow = SetFilterRow | NumberFilterRow;

type RecommendationFilterValueOption = {
    readonly value: string;
    readonly label: string;
};

type SetLeafOp =
    | 'has_tag'
    | 'has_role'
    | 'office_name_is'
    | 'gender_is'
    | 'primary_language_is'
    | 'postal_code_is';

type StatusLeafOp =
    | 'availability_preference_is'
    | 'schedule_availability_is'
    | 'overtime_status_is';

type BooleanLeafOp = 'is_preferred';

type SetParamName =
    | 'tag'
    | 'role'
    | 'office_name'
    | 'gender'
    | 'language'
    | 'postal_code';

type NumberLeafOp =
    | 'max_distance'
    | 'max_contact_attempts'
    | 'star_rating_in_range';

type NumberParamName =
    | 'max_distance_mi'
    | 'max_attempts'
    | 'min_rating'
    | 'max_rating';

type SetDimensionConfig = {
    readonly label: string;
    readonly kind: 'set';
    readonly leafOp: SetLeafOp;
    readonly paramName: SetParamName;
    readonly supportedOperators: Array<SetFilterOperator>;
};

type StatusDimensionConfig = {
    readonly label: string;
    readonly kind: 'status';
    readonly leafOp: StatusLeafOp;
    readonly paramName: 'status';
    readonly supportedOperators: Array<SetFilterOperator>;
    readonly values: Array<RecommendationFilterValueOption>;
};

type NumberDimensionConfig = {
    readonly label: string;
    readonly kind: 'number';
    readonly leafOp: NumberLeafOp;
    readonly paramName: NumberParamName;
    readonly supportedOperators: Array<NumberFilterOperator>;
};

type BooleanDimensionConfig = {
    readonly label: string;
    readonly kind: 'boolean';
    readonly leafOp: BooleanLeafOp;
    readonly supportedOperators: Array<SetFilterOperator>;
    readonly values: Array<RecommendationFilterValueOption>;
};

type RecommendationFilterDimensionConfig =
    | SetDimensionConfig
    | StatusDimensionConfig
    | NumberDimensionConfig
    | BooleanDimensionConfig;

export const RECOMMENDATION_FILTER_DIMENSIONS = {
    tags: {
        label: 'Tags',
        kind: 'set',
        leafOp: 'has_tag',
        paramName: 'tag',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
    },
    roles: {
        label: 'Roles',
        kind: 'set',
        leafOp: 'has_role',
        paramName: 'role',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
    },
    office: {
        label: 'Office',
        kind: 'set',
        leafOp: 'office_name_is',
        paramName: 'office_name',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
    },
    gender: {
        label: 'Gender',
        kind: 'set',
        leafOp: 'gender_is',
        paramName: 'gender',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
    },
    language: {
        label: 'Language',
        kind: 'set',
        leafOp: 'primary_language_is',
        paramName: 'language',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
    },
    zip: {
        label: 'Zip',
        kind: 'set',
        leafOp: 'postal_code_is',
        paramName: 'postal_code',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
    },
    preferred_availability: {
        label: 'Preferred Availability',
        kind: 'status',
        leafOp: 'availability_preference_is',
        paramName: 'status',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
        values: [
            { value: 'available', label: 'Available' },
            { value: 'partial', label: 'Partial' },
            { value: 'unavailable', label: 'Unavailable' },
            { value: 'unknown', label: 'Unknown' },
        ],
    },
    scheduled_availability: {
        label: 'Scheduled Availability',
        kind: 'status',
        leafOp: 'schedule_availability_is',
        paramName: 'status',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
        values: [
            { value: 'available', label: 'Available' },
            { value: 'has_shift_conflict', label: 'Has shift conflict' },
        ],
    },
    overtime: {
        label: 'Overtime',
        kind: 'status',
        leafOp: 'overtime_status_is',
        paramName: 'status',
        supportedOperators: [
            'includes_any',
            'includes_all',
            'excludes_any',
            'excludes_all',
            'is',
            'is_not',
        ],
        values: [
            { value: 'within_limit', label: 'Within limit' },
            { value: 'overtime', label: 'Overtime' },
        ],
    },
    preferred: {
        label: 'Preferred',
        kind: 'boolean',
        leafOp: 'is_preferred',
        supportedOperators: ['includes_any', 'excludes_any', 'is', 'is_not'],
        values: [{ value: 'preferred', label: 'Preferred' }],
    },
    distance: {
        label: 'Distance',
        kind: 'number',
        leafOp: 'max_distance',
        paramName: 'max_distance_mi',
        supportedOperators: ['lte'],
    },
    contact_attempts: {
        label: 'Contact Attempts',
        kind: 'number',
        leafOp: 'max_contact_attempts',
        paramName: 'max_attempts',
        supportedOperators: ['lte'],
    },
    star_rating: {
        label: 'Star Rating',
        kind: 'number',
        leafOp: 'star_rating_in_range',
        paramName: 'min_rating',
        supportedOperators: ['gte', 'lte', 'range'],
    },
} satisfies Record<
    SetOrStatusDimension | NumberDimension,
    RecommendationFilterDimensionConfig
>;

const FACET_COLUMN_TO_RECOMMENDATION_DIMENSION: ReadonlyMap<
    string,
    {
        readonly dimension: SetOrStatusDimension;
        readonly valueKind: 'array' | 'single';
    }
> = new Map([
    ['caregiver_tags', { dimension: 'tags', valueKind: 'array' }],
    ['roles', { dimension: 'roles', valueKind: 'array' }],
    ['office_name', { dimension: 'office', valueKind: 'single' }],
    ['gender', { dimension: 'gender', valueKind: 'single' }],
    ['primary_language', { dimension: 'language', valueKind: 'single' }],
    ['postal_code', { dimension: 'zip', valueKind: 'single' }],
    [
        'availability_preference',
        { dimension: 'preferred_availability', valueKind: 'single' },
    ],
    [
        'schedule_availability',
        { dimension: 'scheduled_availability', valueKind: 'single' },
    ],
    ['overtime_status', { dimension: 'overtime', valueKind: 'single' }],
    ['is_preferred', { dimension: 'preferred', valueKind: 'single' }],
]);

export function buildRecommendationFilterDsl(
    rows: ReadonlyArray<RecommendationFilterRow>,
): RecommendationFilterNode | undefined {
    const subtrees: Array<RecommendationFilterNode> = [];

    for (const row of rows) {
        const subtree = isNumberFilterRow(row)
            ? buildNumberFilterSubtree(row)
            : buildSetFilterSubtree(row);
        if (subtree) {
            subtrees.push(subtree);
        }
    }

    if (subtrees.length === 0) {
        return undefined;
    }

    return { op: 'and', children: subtrees };
}

export function buildInhouseRecommendationV2RequestParams(params: {
    readonly outreachId: string | undefined;
    readonly alwaysIncludeCaregiverIds: Array<string> | undefined;
    readonly rows: ReadonlyArray<RecommendationFilterRow>;
}): InhouseRecommendationRequestParams {
    const filter = buildRecommendationFilterDsl(params.rows);

    return {
        outreach_id: params.outreachId,
        always_include_caregiver_ids: params.alwaysIncludeCaregiverIds,
        ...(filter ? { filter } : {}),
    };
}

export function buildInhouseRecommendationV2RequestParamsFromFacets(params: {
    readonly outreachId?: string | null;
    readonly alwaysIncludeCaregiverIds?: Array<string>;
    readonly columnFilters: ColumnFiltersState;
    readonly maxDistanceMi?: number | null;
    readonly maxContactAttempts?: number | null;
    readonly minStarRating?: number | null;
    readonly maxStarRating?: number | null;
}): InhouseRecommendationRequestParams {
    const rows = buildRowsFromFacetColumnFilters(params.columnFilters);

    addPositiveNumberRow(rows, 'distance', params.maxDistanceMi ?? null);
    addPositiveIntegerRow(
        rows,
        'contact_attempts',
        params.maxContactAttempts ?? null,
    );
    addStarRatingBoundsRows(
        rows,
        params.minStarRating ?? null,
        params.maxStarRating ?? null,
    );

    return buildInhouseRecommendationV2RequestParams({
        outreachId: params.outreachId ?? undefined,
        alwaysIncludeCaregiverIds: params.alwaysIncludeCaregiverIds,
        rows,
    });
}

function buildRowsFromFacetColumnFilters(
    columnFilters: ColumnFiltersState,
): Array<RecommendationFilterRow> {
    const rows: Array<RecommendationFilterRow> = [];

    for (const filter of columnFilters) {
        const config = FACET_COLUMN_TO_RECOMMENDATION_DIMENSION.get(filter.id);
        if (!config) {
            continue;
        }
        const { dimension, valueKind } = config;

        const selection = normalizeFacetSelectionValue(filter.value);
        if (selection.include.length > 0) {
            rows.push({
                id: `facet-${dimension}-include`,
                dimension,
                operator:
                    valueKind === 'array' && selection.includeMode === 'all'
                        ? 'includes_all'
                        : 'includes_any',
                values: selection.include,
            });
        }

        if (selection.exclude.length > 0) {
            rows.push({
                id: `facet-${dimension}-exclude`,
                dimension,
                operator:
                    valueKind === 'array' && selection.excludeMode === 'all'
                        ? 'excludes_all'
                        : 'excludes_any',
                values: selection.exclude,
            });
        }
    }

    return rows;
}

function buildSetFilterSubtree(
    row: SetFilterRow,
): RecommendationFilterNode | undefined {
    const values = normalizeRowValues(row.values);
    if (values.length === 0) {
        return undefined;
    }

    const leaves = values.map((value) => buildSetLeaf(row.dimension, value));
    switch (row.operator) {
        case 'includes_any':
            return leaves.length === 1
                ? (leaves[0] ?? undefined)
                : { op: 'or', children: leaves };
        case 'includes_all':
            return leaves.length === 1
                ? (leaves[0] ?? undefined)
                : { op: 'and', children: leaves };
        case 'excludes_any':
            return leaves.length === 1
                ? { op: 'not', children: leaves.slice(0, 1) }
                : { op: 'not', children: [{ op: 'or', children: leaves }] };
        case 'excludes_all':
            return leaves.length === 1
                ? { op: 'not', children: leaves.slice(0, 1) }
                : { op: 'not', children: [{ op: 'and', children: leaves }] };
        case 'is':
            return leaves.length === 1
                ? (leaves[0] ?? undefined)
                : { op: 'or', children: leaves };
        case 'is_not':
            return leaves.length === 1
                ? { op: 'not', children: leaves.slice(0, 1) }
                : { op: 'not', children: [{ op: 'or', children: leaves }] };
        default:
            assertNever(row.operator);
    }
}

function buildSetLeaf(
    dimension: SetOrStatusDimension,
    value: string,
): RecommendationFilterNode {
    const config = RECOMMENDATION_FILTER_DIMENSIONS[dimension];
    if (config.kind === 'boolean') {
        return { op: config.leafOp };
    }
    if (config.kind === 'status') {
        const status =
            dimension === 'preferred_availability' &&
            value === 'partial_conflict'
                ? 'partial'
                : value;
        return { op: config.leafOp, status };
    }

    return {
        op: config.leafOp,
        [config.paramName]: value,
    };
}

function buildNumberFilterSubtree(
    row: NumberFilterRow,
): RecommendationFilterNode | undefined {
    if (row.value === null) {
        return undefined;
    }

    switch (row.dimension) {
        case 'distance':
            return row.operator === 'lte' && row.value > 0
                ? { op: 'max_distance', max_distance_mi: row.value }
                : undefined;
        case 'contact_attempts':
            return row.operator === 'lte' &&
                row.value > 0 &&
                Number.isInteger(row.value)
                ? { op: 'max_contact_attempts', max_attempts: row.value }
                : undefined;
        case 'star_rating':
            return buildStarRatingSubtree(row);
        default:
            assertNever(row.dimension);
    }
}

function buildStarRatingSubtree(
    row: NumberFilterRow,
): RecommendationFilterNode | undefined {
    if (!isValidStarRating(row.value)) {
        return undefined;
    }

    switch (row.operator) {
        case 'gte':
            return { op: 'star_rating_in_range', min_rating: row.value };
        case 'lte':
            return { op: 'star_rating_in_range', max_rating: row.value };
        case 'range':
            if (
                row.maxValue === undefined ||
                row.maxValue === null ||
                !isValidStarRating(row.maxValue) ||
                row.value > row.maxValue
            ) {
                return undefined;
            }
            return {
                op: 'star_rating_in_range',
                min_rating: row.value,
                max_rating: row.maxValue,
            };
        default:
            assertNever(row.operator);
    }
}

function normalizeFacetSelectionValue(value: unknown): {
    readonly include: Array<string>;
    readonly exclude: Array<string>;
    readonly includeMode?: FacetFilterMode;
    readonly excludeMode?: FacetFilterMode;
} {
    if (Array.isArray(value)) {
        return {
            include: normalizeUnknownRowValues(value),
            exclude: [],
        };
    }

    if (!isReadonlyRecord(value)) {
        return {
            include: typeof value === 'string' ? [value] : [],
            exclude: [],
        };
    }

    const includeMode = value['includeMode'];
    const excludeMode = value['excludeMode'];
    return {
        include: normalizeUnknownRowValues(value['include']),
        exclude: normalizeUnknownRowValues(value['exclude']),
        includeMode: isFacetFilterMode(includeMode) ? includeMode : undefined,
        excludeMode: isFacetFilterMode(excludeMode) ? excludeMode : undefined,
    };
}

function normalizeUnknownRowValues(values: unknown): Array<string> {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .filter((value) => typeof value === 'string')
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
}

function addPositiveNumberRow(
    rows: Array<RecommendationFilterRow>,
    dimension: NumberDimension,
    value: number | null,
) {
    if (value !== null && value > 0) {
        rows.push({
            id: `default-${dimension}`,
            dimension,
            operator: 'lte',
            value,
        });
    }
}

function addPositiveIntegerRow(
    rows: Array<RecommendationFilterRow>,
    dimension: NumberDimension,
    value: number | null,
) {
    if (value !== null && value > 0 && Number.isInteger(value)) {
        rows.push({
            id: `default-${dimension}`,
            dimension,
            operator: 'lte',
            value,
        });
    }
}

function addStarRatingBoundsRows(
    rows: Array<RecommendationFilterRow>,
    minRating: number | null,
    maxRating: number | null,
) {
    const hasValidMinRating = isValidStarRating(minRating);
    const hasValidMaxRating = isValidStarRating(maxRating);

    if (hasValidMinRating && hasValidMaxRating && minRating <= maxRating) {
        rows.push({
            id: 'facet-star-rating',
            dimension: 'star_rating',
            operator: 'range',
            value: minRating,
            maxValue: maxRating,
        });
        return;
    }

    if (hasValidMinRating && maxRating === null) {
        rows.push({
            id: 'facet-star-rating-min',
            dimension: 'star_rating',
            operator: 'gte',
            value: minRating,
        });
        return;
    }

    if (hasValidMaxRating && minRating === null) {
        rows.push({
            id: 'facet-star-rating-max',
            dimension: 'star_rating',
            operator: 'lte',
            value: maxRating,
        });
    }
}

function normalizeRowValues(
    values: ReadonlyArray<string> | undefined,
): Array<string> {
    return (values ?? [])
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
}

function isNumberFilterRow(
    row: RecommendationFilterRow,
): row is NumberFilterRow {
    return RECOMMENDATION_FILTER_DIMENSIONS[row.dimension].kind === 'number';
}

function isValidStarRating(value: number | null): value is number {
    return (
        value !== null && Number.isInteger(value) && value >= 1 && value <= 5
    );
}

function isReadonlyRecord(
    value: unknown,
): value is Readonly<Record<string, unknown>> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isFacetFilterMode(value: unknown): value is FacetFilterMode {
    return value === 'any' || value === 'all';
}

function assertNever(value: never): never {
    throw new Error(`Unexpected recommendation filter value: ${value}`);
}
